import {
  Component,
  forwardRef,
  OnInit,
  input,
  viewChild,
  DestroyRef,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';

import { FormsModule, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  NgbDateAdapter,
  NgbDateParserFormatter,
  NgbDateStruct,
  NgbInputDatepicker
} from '@ng-bootstrap/ng-bootstrap';

import moment from 'moment';
import { TranslatePipe } from '@ngx-translate/core';
import { AsyncPipe } from '@angular/common';
import { SvgIconComponent } from 'angular-svg-icon';
import { BaseControl } from '../base-control';
import { AppFormFieldControl } from '../../form-field/form-field-control/form-field-control';

import { DateStringAdapter } from './date-string-adapter.service';
import { DateParserFormatter } from './date-parser-formatter.service';

@Component({
  selector: 'app-date',
  templateUrl: './date.component.html',
  styleUrls: ['./date.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateComponent),
      multi: true
    },
    {
      provide: AppFormFieldControl,
      useExisting: forwardRef(() => DateComponent)
    },
    {
      provide: NgbDateAdapter,
      useClass: DateStringAdapter
    },
    {
      provide: NgbDateParserFormatter,
      useClass: DateParserFormatter
    }
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgbInputDatepicker,
    FormsModule,
    SvgIconComponent,
    AsyncPipe,
    TranslatePipe
  ]
})
export class DateComponent extends BaseControl<string> implements OnInit {
  #destroyRef = inject(DestroyRef);
  public calendarStartDate$: Observable<NgbDateStruct>;

  readonly ngControl = viewChild(NgControl);
  readonly minDate = input<NgbDateStruct>({
    year: moment().year() - 100,
    month: 1,
    day: 1
  });
  readonly maxDate = input<NgbDateStruct>(undefined);
  readonly startDate = input<NgbDateStruct>(undefined);
  readonly readonly = input<boolean>(undefined);
  readonly container = input<'body' | null>(null);
  readonly placeholder = input('date.format_l');

  get errors() {
    const ngControl = this.ngControl();
    if (!ngControl.errors) return null;
    if (ngControl.errors.ngbDate?.invalid?.length) {
      const { ngbDate, invalid, ...rest } = ngControl.errors;
      let errorMsg;

      if (new Date(ngbDate?.minDate?.actual).getTime() <= Date.now()) {
        if (
          new Date().toDateString() ==
          new Date(ngbDate?.minDate?.actual).toDateString()
        ) {
          errorMsg = { dateInPresentOrToday: true };
        } else {
          errorMsg = { dateInPast: true };
        }
      } else {
        errorMsg = { invalidDate: invalid };
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return {
        ...rest,
        ...errorMsg,
        ...ngbDate
      };
    }

    return ngControl.errors;
  }

  public ngOnInit() {
    this.ngControl()
      .statusChanges.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.stateChanges.next());
    this.calendarStartDate$ = this.ngControl().valueChanges.pipe(
      map(value => {
        const startDate = this.startDate();
        return !startDate || !!value ? undefined : startDate;
      })
    );
  }
}
